// IndexedDB Wrapper for handling large file storage efficiently
// Solves localStorage quota limits by using the browser's database for binary data

const DB_NAME = 'StoneBeamFilesDB';
const DB_VERSION = 1;
const STORE_NAME = 'files';

const openDB = () => {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser."));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error("IndexedDB error:", event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
  });
};

/**
 * Save a file to IndexedDB
 * @param {string} id - Unique ID for the file
 * @param {File|Blob} file - The file object to store
 * @returns {Promise<string>} - Resolves with the ID on success
 */
export const saveFileToIDB = async (id, file) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const record = {
        id,
        file: file, // IndexedDB can store Blobs directly
        name: file.name,
        type: file.type,
        size: file.size,
        createdAt: new Date().toISOString()
      };

      const request = store.put(record);

      request.onsuccess = () => resolve(id);
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (error) {
    throw new Error(`Storage Error: ${error.message}`);
  }
};

/**
 * Retrieve a file from IndexedDB
 * @param {string} id - File ID
 * @returns {Promise<object>} - The file record including the Blob
 */
export const getFileFromIDB = async (id) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          resolve(null); // File not found
        }
      };
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (error) {
    console.error("Failed to get file from IDB:", error);
    return null;
  }
};

/**
 * Delete a file from IndexedDB
 * @param {string} id - File ID
 */
export const deleteFileFromIDB = async (id) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (error) {
    console.error("Failed to delete file from IDB:", error);
  }
};

/**
 * Get estimate of storage usage
 * @returns {Promise<object>} - usage and quota in bytes
 */
export const getStorageEstimate = async () => {
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const { usage, quota } = await navigator.storage.estimate();
      return { usage, quota };
    } catch (error) {
      console.error("Storage estimate error:", error);
    }
  }
  return { usage: 0, quota: 0 };
};

/**
 * Clean up files older than X days
 * @param {number} days - Age in days
 * @returns {Promise<number>} - Number of files deleted
 */
export const cleanupOldFiles = async (days = 30) => {
  try {
    const db = await openDB();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    const cutoffISO = cutoffDate.toISOString();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('createdAt');
      const range = IDBKeyRange.upperBound(cutoffISO);
      
      const request = index.openCursor(range);
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (error) {
    console.error("Cleanup error:", error);
    return 0;
  }
};

/**
 * Count total files in storage
 */
export const getFileCount = async () => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(0);
      });
    } catch (e) { return 0; }
}